import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  CheckCircle, XCircle, AlertTriangle, Plus, ShoppingCart, Link2,
} from 'lucide-react';

export function DraftOrdersTab({
  draftOrders,
  onRejectDraftOrder,
  onApproveDraftOrder,
  onOpenQuickCreate,
}) {
  if (draftOrders.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No pending draft orders</p>
            <p className="text-sm">Upload a purchase order document to extract sales order data</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {draftOrders.map((draft) => (
        <Card key={draft.id} data-testid={`draft-order-${draft.id}`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  PO: {draft.customer_po_number || 'Unknown'}
                </CardTitle>
                <CardDescription>
                  {draft.customer_name || 'Unknown Customer'} • {draft.items?.length || 0} line items
                  {draft.unmatched_items_count > 0 && (
                    <span className="text-orange-600 ml-2">
                      ({draft.unmatched_items_count} need part creation)
                    </span>
                  )}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => onRejectDraftOrder(draft.id)}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Reject
                </Button>
                <Button
                  size="sm"
                  disabled={draft.unmatched_items_count > 0}
                  onClick={() => onApproveDraftOrder(draft.id)}
                  data-testid={`approve-order-${draft.id}`}
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Approve & Create Order
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>Customer P/N</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead>Matched Part</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {draft.items?.map((item, idx) => (
                  <TableRow 
                    key={item.id} 
                    className={item.requires_new_part ? 'bg-orange-50' : ''}
                  >
                    <TableCell className="text-muted-foreground">{item.line_number || idx + 1}</TableCell>
                    <TableCell className="font-mono text-sm">{item.customer_part_no || '-'}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{item.description || '-'}</TableCell>
                    <TableCell className="text-right">{item.quantity || '-'}</TableCell>
                    <TableCell className="text-right">
                      {item.unit_price ? `₹${item.unit_price.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell>
                      {item.matched_part_id ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          <Link2 className="h-3 w-3 mr-1" />
                          {item.matched_part_internal_no}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          No Match
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.requires_new_part && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onOpenQuickCreate(draft.id, item)}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Create Part
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            <div className="mt-4 flex justify-end">
              <div className="text-right space-y-1">
                <p className="text-sm text-muted-foreground">
                  Subtotal: <span className="font-medium text-foreground">
                    {draft.currency || 'INR'} {draft.subtotal?.toFixed(2) || '0.00'}
                  </span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  );
}
